import React from "react";
import { useNavigate } from "react-router-dom";
import { StatusBar, BottomNav } from "../components/Shell";
import { useAppStore } from "../components/AppStore";
import Ico from "../components/Ico";
import { currentUser, rankTiers } from "../mock";
import { Settings as SettingsIcon, Pencil, DollarSign, CheckCircle2, Award, Trophy, BarChart3, PiggyBank, GraduationCap, Bell, ChevronRight, LogOut, Star, Crown } from "lucide-react";

export default function Profile() {
  const navigate = useNavigate();
  const { rankIndex, jobsDone, earnedTotal } = useAppStore();
  const rank = rankTiers[rankIndex];
  const nextRank = rankTiers[rankIndex + 1];
  const nextName = nextRank ? `${nextRank.title[0] + nextRank.title.slice(1).toLowerCase()} (${nextRank.tier})` : "CEO (Diamond)";
  const pct = nextRank ? Math.min(100, Math.round((jobsDone / nextRank.jobs) * 100)) : 100;

  const menu = [
    { icon: Crown, label: "Rank & Rewards", tint: "#f5911e", to: "/ranks" },
    { icon: Trophy, label: "Achievements", tint: "#e6b325", to: "/profile" },
    { icon: BarChart3, label: "Performance Reviews", tint: "#3b57c9", to: "/profile" },
    { icon: PiggyBank, label: "Piggy Bank", tint: "#f5911e", to: "/earnings" },
    { icon: GraduationCap, label: "Money Academy", tint: "#8b5cf6", to: "/academy" },
    { icon: Bell, label: "Notifications", tint: "#22a7c9", to: "/notifications" },
    { icon: SettingsIcon, label: "Settings", tint: "#7b86ab", to: "/settings" },
  ];

  return (
    <div className="gd-screen bg-[#eef1fb]">
      <div className="flex-shrink-0 px-5 pb-16" style={{ background: "linear-gradient(160deg,#3b57c9,#2b3f96)", borderBottomLeftRadius: 30, borderBottomRightRadius: 30 }}>
        <StatusBar dark={false} />
        <div className="flex justify-end">
          <button onClick={() => navigate("/settings")} className="gd-tap grid h-10 w-10 place-items-center rounded-full bg-white/15"><SettingsIcon size={18} color="#fff" /></button>
        </div>
        <div className="mt-1 flex flex-col items-center">
          <div className="relative">
            <div className="grid h-20 w-20 place-items-center rounded-3xl bg-white text-3xl font-black text-[#2b3f96]">{currentUser.initial}</div>
            <button className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-full bg-[#f5911e] text-white shadow"><Pencil size={13} /></button>
          </div>
          <h1 className="mt-3 text-2xl font-black text-white">{currentUser.name}</h1>
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-sm font-semibold text-white/80">{rank.title[0] + rank.title.slice(1).toLowerCase()}</span>
            <span className="rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background: rank.color }}>{rank.tier}</span>
            <span className="text-sm font-semibold text-white/60">· since {currentUser.memberSince}</span>
          </div>
        </div>
      </div>

      <div className="gd-scroll -mt-10 px-4 pb-6 gd-stagger">
        {/* level progress */}
        <div className="gd-card p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-extrabold text-[#1f2a52]">Rank Progress</span>
            <span className="font-bold text-[#8b93b5]">{jobsDone} jobs · ${earnedTotal}</span>
          </div>
          <div className="mt-2 h-2.5 rounded-full bg-[#eef1fb]"><div className="h-2.5 rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#f5a63a,#f5911e)" }} /></div>
          <p className="mt-1.5 text-[11px] font-semibold text-[#9aa3c0]">{nextRank ? `${Math.max(0, nextRank.jobs - jobsDone)} jobs or $${Math.max(0, nextRank.dollars - earnedTotal)} to reach ${nextName}` : "Top rank — Neighborhood CEO!"}</p>
        </div>

        {/* stats */}
        <div className="mt-3 grid grid-cols-3 gap-3">
          <PStat icon={DollarSign} tint="#4fae3f" v={`$${earnedTotal}`} l="Earned" />
          <PStat icon={CheckCircle2} tint="#3b57c9" v={jobsDone} l="Tasks" />
          <PStat icon={Award} tint="#e6b325" v={currentUser.badges} l="Badges" />
        </div>

        {/* rating strip */}
        <div className="gd-card mt-3 flex items-center justify-between p-4">
          <div className="flex items-center gap-2"><Star size={20} fill="#f5b301" color="#f5b301" /><span className="text-lg font-black text-[#1f2a52]">{currentUser.rating}</span><span className="text-xs font-semibold text-[#9aa3c0]">avg rating</span></div>
          <span className="rounded-full bg-[#fff5e6] px-3 py-1 text-xs font-bold text-[#e97f0c]">Top Rated ⭐</span>
        </div>

        {/* menu */}
        <div className="gd-card mt-3 divide-y divide-[#f0f2fa] p-1">
          {menu.map((m) => (
            <button key={m.label} onClick={() => navigate(m.to)} className="gd-tap flex w-full items-center gap-3 px-3 py-3.5">
              <div className="grid h-10 w-10 place-items-center rounded-2xl" style={{ background: `${m.tint}18` }}><m.icon size={18} color={m.tint} /></div>
              <span className="flex-1 text-left text-sm font-bold text-[#1f2a52]">{m.label}</span>
              <ChevronRight size={18} className="text-[#c7cde6]" />
            </button>
          ))}
        </div>

        <button onClick={() => navigate("/auth")} className="gd-tap mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#fdece9] py-3.5 font-extrabold text-[#e0533d]">
          <LogOut size={18} /> Sign Out
        </button>
      </div>

      <BottomNav />
    </div>
  );
}

function PStat({ icon: Icon, tint, v, l }) {
  return (
    <div className="flex flex-col items-center rounded-[18px] bg-white py-3 shadow-[0_8px_20px_rgba(31,42,82,0.05)]">
      <Icon size={18} color={tint} />
      <span className="mt-1 text-xl font-black text-[#1f2a52]">{v}</span>
      <span className="text-[10px] font-semibold text-[#9aa3c0]">{l}</span>
    </div>
  );
}
